import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import { isNewsletterUrl } from '../lib/newsletterUrl';
import { activeThought } from '../lib/thoughts';
import { firstHotTake } from '../lib/hotTakes';
// ── useIsMobile ──────────────────────────────────────────────────────────────
export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}


// ── useThoughtsFeed ──────────────────────────────────────────────────────────
// Merges Hot Take / Thought (from community_thought_posts, text pulled live
// from user_profiles) with Substack posts. No FK joins — fetch profiles
// separately per established codebase pattern.
export function useThoughtsFeed() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      const { data: posts } = await supabase
        .from('active_community_thought_posts')
        .select('id, user_id, post_type, is_public, expires_at, created_at, updated_at')
        .eq('is_public', true)
        .order('updated_at', { ascending: false })
        .limit(100);

      const userIds = [...new Set((posts || []).map(p => p.user_id))];
      let profileMap = {};
      if (userIds.length) {
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('user_id, username, avatar_url, hot_take, hot_takes, current_thought, thought_expires_at, substack_url')
          .in('user_id', userIds);
        (profiles || []).forEach(p => { profileMap[p.user_id] = p; });
      }

      // Pull live text off the profile at render time — drop cards whose
      // source text has since been cleared (true live mirror, no snapshot).
      const liveCards = (posts || [])
        .map(post => {
          const profile = profileMap[post.user_id];
          if (!profile) return null;
          if (post.post_type === 'hot_take') {
            // One card per reader, showing their first hot take (slide 1 of
            // the profile block). Both columns are selected because a reader
            // who has not saved since the list arrived still has theirs in
            // hot_take (src/lib/hotTakes.js).
            const text = firstHotTake(profile);
            if (!text) return null;
            return { ...post, text, username: profile.username, avatar_url: profile.avatar_url };
          }
          if (post.post_type === 'thought') {
            const text = activeThought(profile);
            if (!text) return null;
            return { ...post, text, username: profile.username, avatar_url: profile.avatar_url };
      }
          return null;
        })
        .filter(Boolean);

      // Like/comment counts
      const ids = liveCards.map(c => c.id);
      let likeCounts = {}, commentCounts = {};
      if (ids.length) {
        const { data: likes } = await supabase.from('community_thought_likes').select('post_id').in('post_id', ids);
        (likes || []).forEach(l => { likeCounts[l.post_id] = (likeCounts[l.post_id] || 0) + 1; });
        const { data: comments } = await supabase.from('community_thought_comments').select('post_id').in('post_id', ids);
        (comments || []).forEach(c => { commentCounts[c.post_id] = (commentCounts[c.post_id] || 0) + 1; });
      }

      let combined = liveCards.map(c => ({
        ...c, like_count: likeCounts[c.id] || 0, comment_count: commentCounts[c.id] || 0,
      }));

      // Substack — always fetched alongside hot_take/thought now, not gated
      // by filter, so counts and the "All" view stay accurate regardless of
      // which chip is active.
      // The column went unvalidated for a long time and still holds emails and
      // bare handles. Narrow in SQL first — filtering only in JS would let junk
      // rows eat the row budget and starve the feed of real cards.
      const { data: substackProfiles } = await supabase
        .from('user_profiles')
        .select('user_id, username, avatar_url, substack_url, updated_at')
        .not('substack_url', 'is', null)
        .neq('substack_url', '')
        .or('substack_url.ilike.*substack.com*,substack_url.ilike.*medium.com*')
        .order('updated_at', { ascending: false })
        .limit(30);
      (substackProfiles || []).forEach(p => {
        // ilike is the coarse pass; this is the one that decides.
        if (!isNewsletterUrl(p.substack_url)) return;
        combined.push({
          id: `substack-${p.user_id}`, post_type: 'substack', user_id: p.user_id,
          username: p.username, avatar_url: p.avatar_url, substack_url: p.substack_url,
          updated_at: p.updated_at, like_count: 0, comment_count: 0,
        });
      });

      // Interleave by recency instead of grouping all substacks at the tail —
      // otherwise they get pushed past the initial paginated window on "All".
      combined.sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));

      setItems(combined);
    } catch (err) {
      console.error('useThoughtsFeed error:', err.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  return { items, loading, refetch: fetchAll };
}

// ── useThoughtComments ───────────────────────────────────────────────────────
export function useThoughtComments(postId) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!postId) return;
    fetchComments();

    const channel = supabase
      .channel(`thought-comments-${postId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'community_thought_comments', filter: `post_id=eq.${postId}` },
        payload => { fetchOneCommentProfile(payload.new); })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [postId]);

  async function fetchComments() {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('community_thought_comments')
        .select('id, user_id, content, created_at')
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      const userIds = [...new Set((data || []).map(c => c.user_id))];
      let profileMap = {};
      if (userIds.length) {
        const { data: profiles } = await supabase.from('user_profiles').select('user_id, username, avatar_url').in('user_id', userIds);
        (profiles || []).forEach(p => { profileMap[p.user_id] = p; });
      }
      setComments((data || []).map(c => ({ ...c, username: profileMap[c.user_id]?.username, avatar_url: profileMap[c.user_id]?.avatar_url })));
    } catch (err) {
      console.error('useThoughtComments error:', err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchOneCommentProfile(row) {
    const { data: profile } = await supabase.from('user_profiles').select('user_id, username, avatar_url').eq('user_id', row.user_id).maybeSingle();
    setComments(prev => prev.some(c => c.id === row.id) ? prev : [...prev, { ...row, username: profile?.username, avatar_url: profile?.avatar_url }]);
  }

  async function addComment(content) {
    if (!content.trim()) return;
    const user = await getCurrentUser();
    if (!user) return;
    const { error } = await supabase.from('community_thought_comments').insert({ post_id: postId, user_id: user.id, content: content.trim() });
    if (error) console.error('addComment error:', error.message);
  }

  return { comments, loading, addComment };
}

// ── useToggleLike ────────────────────────────────────────────────────────────
export function useToggleLike(postId, initialCount = 0) {
  const [liked, setLiked]   = useState(false);
  const [count, setCount]   = useState(initialCount);
  const [busy, setBusy]     = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const user = await getCurrentUser();
      if (!user || !postId) return;
      const { data } = await supabase.from('community_thought_likes').select('post_id').eq('post_id', postId).eq('user_id', user.id).maybeSingle();
      if (!cancelled) setLiked(!!data);
    })();
    return () => { cancelled = true; };
  }, [postId]);

  async function toggle() {
    if (busy) return;
    setBusy(true);
    const user = await getCurrentUser();
    if (!user) { setBusy(false); return; }

    if (liked) {
      setLiked(false);
      setCount(c => Math.max(0, c - 1));
      await supabase.from('community_thought_likes').delete().eq('post_id', postId).eq('user_id', user.id);
    } else {
      setLiked(true);
      setCount(c => c + 1);
      await supabase.from('community_thought_likes').insert({ post_id: postId, user_id: user.id });
    }
    setBusy(false);
  }

  return { liked, count, toggle, busy };
}

// ── useShopFeed ──────────────────────────────────────────────────────────────
export function useShopFeed() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      const { data: products } = await supabase
        .from('shop_products')
        .select('id, user_id, name, price, emoji, link, image_url, created_at')
        .order('created_at', { ascending: false })
        .limit(200);

      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('user_id, username, avatar_url, shop_url, amazon_storefront');

      const profileMap = {};
      (profiles || []).forEach(p => { profileMap[p.user_id] = p; });

      // Group products by owner, cap 4/shop
      const shopMap = {};
      (products || []).forEach(prod => {
        if (!shopMap[prod.user_id]) shopMap[prod.user_id] = [];
        if (shopMap[prod.user_id].length < 4) shopMap[prod.user_id].push(prod);
      });

      const cards = Object.entries(shopMap).map(([userId, prods]) => {
        const shopUrl = profileMap[userId]?.shop_url || null;
        return {
          type: 'shop', user_id: userId, username: profileMap[userId]?.username,
          avatar_url: profileMap[userId]?.avatar_url, shop_url: shopUrl,
          platform: shopUrl?.toLowerCase().includes('etsy.com') ? 'Etsy' : 'Own Site',
          products: prods,
        };
      });

      // Flatten enabled Amazon Storefront lists — one card per list, raw
      // owner URLs, no Reanlo affiliate tag injected (per spec).
      Object.values(profileMap).forEach(p => {
        (p.amazon_storefront?.lists || []).filter(l => l.enabled).forEach(list => {
          cards.push({
            type: 'amazon_list', user_id: p.user_id, username: p.username, avatar_url: p.avatar_url,
            list_name: list.name, list_icon: list.icon, list_color: list.color, url: list.url,
          });
        });
      });

      setItems(cards);
    } catch (err) {
      console.error('useShopFeed error:', err.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  return { items, loading, refetch: fetchAll };
}

// ── useWatchListenFeed ───────────────────────────────────────────────────────
// Live directory, same pattern as useShopFeed — reads current profile state
// directly (youtube_data / podcast_data / showcase_items), no event/index table.
// No privacy gate exists on these blocks today, so anything present shows.
//
// Returns FLAT, individually-playable items (one row per video / episode /
// showcase piece, not grouped by creator) — the "now showing" + guide-list
// layout needs to feature any single item, not a creator's whole bundle.
// Always fetches everything, unfiltered — filtering for the guide list
// happens client-side in the tab so switching filters never touches
// whatever is currently playing in the featured panel.
export function useWatchListenFeed() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('user_id, username, avatar_url, youtube_data, podcast_data, showcase_items, updated_at')
        .order('updated_at', { ascending: false })
        .limit(200);

      const flat = [];
      (profiles || []).forEach(p => {
        const yt   = p.youtube_data;
        const pod  = p.podcast_data;
        const show = p.showcase_items || [];
        const creator = { user_id: p.user_id, username: p.username, avatar_url: p.avatar_url };

        (yt?.videos || []).slice(0, 3).forEach((v, i) => {
          flat.push({
            ...creator, type: 'youtube', id: `yt-${p.user_id}-${v.video_id || i}`,
            title: v.title || 'YouTube Video', video_id: v.video_id, thumbnail_url: v.thumbnail_url,
            channel_url: yt.channel_url || null,
          });
        });

        (pod?.episodes || []).slice(0, 3).forEach((ep, i) => {
          flat.push({
            ...creator, type: 'podcast', id: `pod-${p.user_id}-${i}`,
            title: ep.title || 'Episode', podcast_name: pod.podcast_name || 'Podcast',
            audio_url: ep.audio_url || null, url: ep.url || null,
            duration: ep.duration || null, date: ep.date || null,
            spotify_url: pod.spotify_url || null, apple_url: pod.apple_url || null,
          });
        });

        show.slice(0, 5).forEach((item, i) => {
          flat.push({
            ...creator, type: 'showcase', id: `show-${p.user_id}-${i}`,
            title: item.title || 'Showcase piece', image_url: item.url, link: item.link || null,
          });
        });
      });

      setItems(flat);
    } catch (err) {
      console.error('useWatchListenFeed error:', err.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  return { items, loading, refetch: fetchAll };
}

// ── useCommunityStats ────────────────────────────────────────────────────────
// Reads the nightly-refreshed snapshot row — cheap read, no live aggregation.
export function useCommunityStats() {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await supabase.from('community_stats_snapshot').select('*').eq('id', 1).maybeSingle();
        setStats(data || null);
      } catch (err) {
        console.error('useCommunityStats error:', err.message);
        setStats(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return { stats, loading };
}

// ── useShelfFeed ─────────────────────────────────────────────────────────────
export function useShelfFeed(filter = 'all') {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, [filter]);

  async function fetchAll() {
    setLoading(true);
    try {
      let q = supabase
        .from('shelf_activity_events')
        .select('id, user_id, event_type, book_id, book_title, book_author, book_cover_url, trope_name, shelf_name, shelf_emoji, created_at')
        .order('created_at', { ascending: false })
        .limit(100);
      if (filter !== 'all') q = q.eq('event_type', filter);
      const { data: events } = await q;

      const userIds = [...new Set((events || []).map(e => e.user_id))];
      let profileMap = {};
      if (userIds.length) {
        const { data: profiles } = await supabase
          .from('user_profiles').select('user_id, username, avatar_url')
          .in('user_id', userIds);
        (profiles || []).forEach(p => { profileMap[p.user_id] = p; });
      }

      setItems((events || []).map(e => ({
        ...e,
        username:   profileMap[e.user_id]?.username,
        avatar_url: profileMap[e.user_id]?.avatar_url,
      })));
    } catch (err) {
      console.error('useShelfFeed error:', err.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  return { items, loading, refetch: fetchAll };
}